/* eslint-disable max-len */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useState, ChangeEvent, KeyboardEvent, FC } from 'react'
import { useForm } from 'react-hook-form'
import { fixedNumber } from '../js/utils'
import { FormInput, defaultValues, constraintValues } from './Simulation'
import { SimulationReq } from '../api/selfApi'
import { useDispatch } from 'react-redux'
import { fetchSimulation } from '../store/modules/simulation'
import '../assets/css/libs.css'
import '../assets/css/style.css'

interface SimulationFormProps {
  setInputErrorMessage: (msg: string) => void
  isPending: boolean
  isResponsive: boolean
}

function getDecimalPointLength(value: string) {
  const numberParts = value.split('.')
  return numberParts[1] ? numberParts[1].length : 0
}

function makeRequest(arg: FormInput): SimulationReq {
  return {
    initial: Number(arg.initial) * 10000,
    addition: Number(arg.addition) * 10000,
    ret: Number(arg.ret),
    risk: Number(arg.risk),
    additionRet: arg.additionRet === undefined ? Number(arg.ret) : Number(arg.additionRet),
    additionRisk: arg.additionRisk === undefined ? Number(arg.risk) : Number(arg.additionRisk),
    depositRet: Number(arg.depositRet),
    months: Number(arg.years) * 12,
  } as SimulationReq
}

export const SimulationForm: FC<SimulationFormProps> = React.memo(
  ({ setInputErrorMessage, isPending, isResponsive }) => {
    const dispatch = useDispatch()
    const { register, handleSubmit, setValue, getValues } = useForm<FormInput>()
    const [showAdditionRiskRet, setShowAdditionRiskRet] = useState(false)

    const handleShowAdditionRiskRet = (val: boolean) => {
      setShowAdditionRiskRet(val)
    }

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, isShow: boolean) => {
      e.preventDefault()
      handleShowAdditionRiskRet(isShow)
    }

    const onSubmit = useCallback(
      (arg: FormInput) => {
        if (Number(arg.ret) === 0 && Number(arg.risk) === 0) {
          setInputErrorMessage(
            '初期投資額の下段にある、リターン（年率）、リスク（年率）を入力してください。'
          )
          return
        }
        if (
          (arg.additionRet !== undefined || arg.additionRisk !== undefined) &&
          (arg.additionRet === undefined || arg.additionRisk === undefined) &&
          Number(arg.additionRet) === 0 &&
          Number(arg.additionRisk) === 0
        ) {
          setInputErrorMessage(
            '個別設定した場合、積立額（毎月）の下段にある、リターン（年率）、リスク（年率）を入力してください。'
          )
          return
        }
        if (Number(arg.initial) === 0 && Number(arg.addition) === 0) {
          setInputErrorMessage('評価額が0円を下回ります。初期投資額と積立額を設定して下さい。')
          return
        }
        const param = makeRequest(arg)
        dispatch(fetchSimulation(param))
      },
      [dispatch, setInputErrorMessage]
    )

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>, upper: number, lower: number, decimalPoint: number) => {
        const inputValue = e.target.value
        const inputName = e.target.name as
          | 'addition'
          | 'initial'
          | 'ret'
          | 'risk'
          | 'additionRet'
          | 'additionRisk'
          | 'depositRet'
          | 'years'
        if (inputValue === '') {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (inputValue.match(/^[0-9]+\.?[0-9]*$/) === null || isNaN(parseFloat(inputValue))) {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (Number(inputValue) > upper) {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (Number(inputValue) < lower) {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (getDecimalPointLength(inputValue) > decimalPoint) {
          if (decimalPoint === 0) {
            setInputErrorMessage('小数点第1位を四捨五入して表示します。')
            setValue(inputName, Math.round(Number(inputValue)), {
              shouldValidate: true,
            })
            return
          } else {
            if (decimalPoint === 1) {
              setInputErrorMessage('小数点第2位を四捨五入して表示します。')
            } else {
              setInputErrorMessage('小数点第3位を四捨五入して表示します。')
            }
            setValue(inputName, Number(fixedNumber(inputValue, decimalPoint)), {
              shouldValidate: true,
            })
            return
          }
        }
        if (inputValue.match(/^0/) !== null) {
          setValue(inputName, Number(inputValue), {
            shouldValidate: true,
          })
        }
      },
      [setValue, setInputErrorMessage]
    )

    const handleUpDownKey = useCallback(
      (
        e: KeyboardEvent<HTMLInputElement>,
        inputName:
          | 'initial'
          | 'addition'
          | 'initial'
          | 'ret'
          | 'risk'
          | 'additionRet'
          | 'additionRisk'
          | 'depositRet'
          | 'years',
        step: number,
        decimalPoint: number
      ) => {
        const value = getValues(inputName)
        const nowValue = value !== undefined ? Number(value) : Number(defaultValues[inputName])

        if (e.key === 'ArrowUp') {
          const calcValue = nowValue + step
          const newValue =
            calcValue <= constraintValues[inputName].upper
              ? Number(fixedNumber(calcValue, decimalPoint))
              : constraintValues[inputName].upper
          setValue(inputName, newValue, { shouldValidate: true })
          e.preventDefault()
          return
        } else if (e.key === 'ArrowDown') {
          const calcValue = nowValue - step
          const newValue =
            calcValue >= constraintValues[inputName].lower
              ? Number(fixedNumber(calcValue, decimalPoint))
              : constraintValues[inputName].lower
          setValue(inputName, newValue, { shouldValidate: true })
          e.preventDefault()
          return
        }
      },
      [getValues, setValue]
    )

    return (
      <React.Fragment>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="p-simulation__grid-1">
            <div className="p-simulation__grid-1__col">
              <div className="c-form-group">
                <span className="c-form-group__sup">初期投資額</span>
                <span className="c-form-group__input c-grid--md-justify-content-start u-size--md-w243px">
                  <span className="c-form-group__text u-size--md-w74px"></span>
                  <input
                    className="c-form__input u-text--right u-size--w140px"
                    type="number"
                    name="initial"
                    step="any"
                    defaultValue={defaultValues.initial}
                    onKeyDown={(e) =>
                      handleUpDownKey(e, 'initial', 10, constraintValues.initial.decimalPoint)
                    }
                    onBlurCapture={(e) =>
                      handleChange(
                        e,
                        constraintValues.initial.upper,
                        constraintValues.initial.lower,
                        constraintValues.initial.decimalPoint
                      )
                    }
                    ref={register({
                      required: true,
                      validate: {
                        isNumber: (value) =>
                          value.match(/^[0-9]+\.?[0-9]*$/) !== null && !isNaN(parseFloat(value)),
                        upper: (value) => Number(value) <= constraintValues.initial.upper,
                        lower: (value) => Number(value) >= constraintValues.initial.lower,
                        decimal: (value) =>
                          getDecimalPointLength(value) <= constraintValues.initial.decimalPoint,
                      },
                    })}
                  />
                  <span className="c-form-group__text">万円</span>
                </span>
              </div>

              <div className="c-grid c-grid--md-justify-content-center">
                <div className="c-grid__col u-size--md-w114px">
                  <div className="c-form-group">
                    <span className="c-form-group__sup notit">リターン（年率）</span>
                    <span className="c-form-group__input">
                      <input
                        className="c-form__input u-text--right u-size--w90px"
                        type="number"
                        name="ret"
                        defaultValue={defaultValues.ret}
                        step="0.1"
                        onKeyDown={(e) =>
                          handleUpDownKey(e, 'ret', 0.1, constraintValues.ret.decimalPoint)
                        }
                        onBlurCapture={(e) =>
                          handleChange(
                            e,
                            constraintValues.ret.upper,
                            constraintValues.ret.lower,
                            constraintValues.ret.decimalPoint
                          )
                        }
                        ref={register({
                          required: true,
                          validate: {
                            isNumber: (value) =>
                              value.match(/^[0-9]+\.?[0-9]*$/) !== null &&
                              !isNaN(parseFloat(value)),
                            upper: (value) => Number(value) <= constraintValues.ret.upper,
                            lower: (value) => Number(value) >= constraintValues.ret.lower,
                            decimal: (value) =>
                              getDecimalPointLength(value) <= constraintValues.ret.decimalPoint,
                          },
                        })}
                      />
                      <span className="c-form-group__text">%</span>
                    </span>
                  </div>
                </div>
                <div className="c-grid__col u-size--md-w114px u-md-ml15px">
                  <div className="c-form-group">
                    <span className="c-form-group__sup notit">リスク（年率）</span>
                    <span className="c-form-group__input">
                      <input
                        className="c-form__input u-text--right u-size--w90px"
                        type="number"
                        name="risk"
                        defaultValue={defaultValues.risk}
                        step="0.1"
                        onKeyDown={(e) =>
                          handleUpDownKey(e, 'risk', 0.1, constraintValues.risk.decimalPoint)
                        }
                        onBlurCapture={(e) =>
                          handleChange(
                            e,
                            constraintValues.risk.upper,
                            constraintValues.risk.lower,
                            constraintValues.risk.decimalPoint
                          )
                        }
                        ref={register({
                          required: true,
                          validate: {
                            isNumber: (value) =>
                              value.match(/^[0-9]+\.?[0-9]*$/) !== null &&
                              !isNaN(parseFloat(value)),
                            upper: (value) => Number(value) <= constraintValues.risk.upper,
                            lower: (value) => Number(value) >= constraintValues.risk.lower,
                            decimal: (value) =>
                              getDecimalPointLength(value) <= constraintValues.risk.decimalPoint,
                          },
                        })}
                      />
                      <span className="c-form-group__text">%</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-simulation__grid-1__col">
              <div className="c-form-group">
                <span className="c-form-group__sup">積立額（毎月）</span>
                <span className="c-form-group__input">
                  <span className="c-form-group__text u-size--md-w74px"></span>
                  <input
                    className="c-form__input u-text--right u-size--w100px u-size--md-w140px"
                    type="number"
                    name="addition"
                    defaultValue={defaultValues.addition}
                    step="0.1"
                    onKeyDown={(e) =>
                      handleUpDownKey(e, 'addition', 0.1, constraintValues.addition.decimalPoint)
                    }
                    onBlurCapture={(e) =>
                      handleChange(
                        e,
                        constraintValues.addition.upper,
                        constraintValues.addition.lower,
                        constraintValues.addition.decimalPoint
                      )
                    }
                    ref={register({
                      required: true,
                      validate: {
                        isNumber: (value) =>
                          value.match(/^[0-9]+\.?[0-9]*$/) !== null && !isNaN(parseFloat(value)),
                        upper: (value) => Number(value) <= constraintValues.addition.upper,
                        lower: (value) => Number(value) >= constraintValues.addition.lower,
                        decimal: (value) =>
                          getDecimalPointLength(value) <= constraintValues.addition.decimalPoint,
                      },
                    })}
                  />
                  <span className="c-form-group__text u-size--md-w64px">万円</span>
                </span>
              </div>
              {!showAdditionRiskRet && (
                <p>
                  <a
                    href="#"
                    className="c-link p-simulation__text-link"
                    onClick={(e) => handleClick(e, true)}
                  >
                    初期投資額とは別に、積立分のリターン（年率）、リスク（年率）を設定する
                  </a>
                </p>
              )}
              {showAdditionRiskRet && (
                <div className="c-grid c-grid--md-justify-content-center">
                  <div className="c-grid__col u-size--md-w114px">
                    <div className="c-form-group">
                      <span className="c-form-group__sup notit">リターン（年率）</span>
                      <span className="c-form-group__input">
                        <input
                          className="c-form__input u-text--right u-size--w90px"
                          type="number"
                          name="additionRet"
                          defaultValue={defaultValues.additionRet}
                          step="0.1"
                          onKeyDown={(e) =>
                            handleUpDownKey(
                              e,
                              'additionRet',
                              0.1,
                              constraintValues.additionRet.decimalPoint
                            )
                          }
                          onBlurCapture={(e) =>
                            handleChange(
                              e,
                              constraintValues.additionRet.upper,
                              constraintValues.additionRet.lower,
                              constraintValues.additionRet.decimalPoint
                            )
                          }
                          ref={register({
                            required: true,
                            validate: {
                              isNumber: (value) =>
                                value.match(/^[0-9]+\.?[0-9]*$/) !== null &&
                                !isNaN(parseFloat(value)),
                              upper: (value) => Number(value) <= constraintValues.additionRet.upper,
                              lower: (value) => Number(value) >= constraintValues.additionRet.lower,
                              decimal: (value) =>
                                getDecimalPointLength(value) <=
                                constraintValues.additionRet.decimalPoint,
                            },
                          })}
                        />
                        <span className="c-form-group__text">%</span>
                      </span>
                    </div>
                  </div>
                  <div className="c-grid__col u-size--md-w114px u-md-ml15px">
                    <div className="c-form-group">
                      <span className="c-form-group__sup notit">リスク（年率）</span>
                      <span className="c-form-group__input">
                        <input
                          className="c-form__input u-text--right u-size--w90px"
                          type="number"
                          name="additionRisk"
                          defaultValue={defaultValues.additionRisk}
                          step="0.1"
                          onKeyDown={(e) =>
                            handleUpDownKey(
                              e,
                              'additionRisk',
                              0.1,
                              constraintValues.additionRisk.decimalPoint
                            )
                          }
                          onBlurCapture={(e) =>
                            handleChange(
                              e,
                              constraintValues.additionRisk.upper,
                              constraintValues.additionRisk.lower,
                              constraintValues.additionRisk.decimalPoint
                            )
                          }
                          ref={register({
                            required: true,
                            validate: {
                              isNumber: (value) =>
                                value.match(/^[0-9]+\.?[0-9]*$/) !== null &&
                                !isNaN(parseFloat(value)),
                              upper: (value) =>
                                Number(value) <= constraintValues.additionRisk.upper,
                              lower: (value) =>
                                Number(value) >= constraintValues.additionRisk.lower,
                              decimal: (value) =>
                                getDecimalPointLength(value) <=
                                constraintValues.additionRisk.decimalPoint,
                            },
                          })}
                        />
                        <span className="c-form-group__text">%</span>
                      </span>
                    </div>
                  </div>
                  <a
                    href="#"
                    className="p-simulation__setting__close"
                    onClick={(e) => handleClick(e, false)}
                  >
                    ×
                  </a>
                </div>
              )}
            </div>

            <div className="p-simulation__grid-1__col">
              <div className="c-form-group">
                <span className="c-form-group__sup">預金金利（年率）</span>
                <span className="c-form-group__input">
                  <span className="c-form-group__text u-size--md-w74px"></span>
                  <input
                    className="c-form__input u-text--right u-size--w100px u-size--md-w140px"
                    type="number"
                    name="depositRet"
                    defaultValue={defaultValues.depositRet}
                    step="0.01"
                    onKeyDown={(e) =>
                      handleUpDownKey(
                        e,
                        'depositRet',
                        0.01,
                        constraintValues.depositRet.decimalPoint
                      )
                    }
                    onBlurCapture={(e) =>
                      handleChange(
                        e,
                        constraintValues.depositRet.upper,
                        constraintValues.depositRet.lower,
                        constraintValues.depositRet.decimalPoint
                      )
                    }
                    ref={register({
                      required: true,
                      validate: {
                        isNumber: (value) =>
                          value.match(/^[0-9]+\.?[0-9]*$/) !== null && !isNaN(parseFloat(value)),
                        upper: (value) => Number(value) <= constraintValues.depositRet.upper,
                        lower: (value) => Number(value) >= constraintValues.depositRet.lower,
                        decimal: (value) =>
                          getDecimalPointLength(value) <= constraintValues.depositRet.decimalPoint,
                      },
                    })}
                  />
                  <span className="c-form-group__text u-size--md-w64px">%</span>
                </span>
              </div>
            </div>

            <div className="p-simulation__grid-1__col">
              <div className="c-form-group">
                <span className="c-form-group__sup">期間</span>
                <span className="c-form-group__input">
                  <span className="c-form-group__text u-size--md-w74px"></span>
                  <input
                    className="c-form__input u-text--right u-size--w80px u-size--md-w140px"
                    type="number"
                    name="years"
                    defaultValue={defaultValues.years}
                    onKeyDown={(e) =>
                      handleUpDownKey(e, 'years', 1, constraintValues.years.decimalPoint)
                    }
                    onBlurCapture={(e) =>
                      handleChange(
                        e,
                        constraintValues.years.upper,
                        constraintValues.years.lower,
                        constraintValues.years.decimalPoint
                      )
                    }
                    ref={register({
                      required: true,
                      validate: {
                        isNumber: (value) =>
                          value.match(/^[0-9]+\.?[0-9]*$/) !== null && !isNaN(parseFloat(value)),
                        upper: (value) => Number(value) <= constraintValues.years.upper,
                        lower: (value) => Number(value) >= constraintValues.years.lower,
                        decimal: (value) =>
                          getDecimalPointLength(value) <= constraintValues.years.decimalPoint,
                      },
                    })}
                  />
                  <span className="c-form-group__text u-size--md-w64px">年</span>
                </span>
              </div>
            </div>
          </div>
          <div className="u-text--center u-mt8px">
            <button
              className="c-button-calc"
              type="submit"
              disabled={isPending}
              id="btn_simulation"
            >
              <React.Fragment>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 164 92">
                  <defs>
                    <filter id="a" x="0" y="0" width="164" height="92" filterUnits="userSpaceOnUse">
                      <feOffset dx="4" dy="4" />
                      <feGaussianBlur result="blur" />
                      <feFlood flood-color="#5a435a" />
                      <feComposite operator="in" in2="blur" />
                      <feComposite in="SourceGraphic" />
                    </filter>
                  </defs>
                  <g filter="url(#a)">
                    <g fill="#fff" className="c-button-calc__bg">
                      <path d="M80 85.854L1.998 55.63V10.001c0-4.411 3.59-8 8.002-8h140.002c4.412 0 8 3.589 8 8V55.63L80 85.854z" />
                      <path
                        d="M80 83.709l76.002-29.449V10.001c0-3.308-2.691-6-6-6H10a6.008 6.008 0 00-6.002 6V54.26L80 83.71m0 4.29L-.002 57V10.001c0-5.524 4.477-10 10.002-10h140.002c5.52 0 10 4.476 10 10V57L80 87.998z"
                        fill="#62a598"
                      />
                    </g>
                  </g>
                </svg>
                <span>計算する</span>
              </React.Fragment>
            </button>
          </div>
        </form>
      </React.Fragment>
    )
  }
)
